/**
 * UUID v4 所需的随机字节长度。
 */
const UUID_V4_BYTE_LENGTH = 16;

/**
 * 获取当前运行环境可用的 Web Crypto 接口。
 *
 * cad-core 同时运行在浏览器与测试环境中，这里先收口能力探测，
 * 避免直接假设 `crypto.randomUUID()` 一定存在。
 *
 * @returns 当前环境可用的 Web Crypto；不可用时返回 `undefined`。
 */
function getCryptoApi(): Crypto | undefined {
  if (typeof globalThis === 'undefined') {
    return undefined;
  }

  const cryptoApi = globalThis.crypto;
  if (!cryptoApi) {
    return undefined;
  }

  return cryptoApi;
}

/**
 * 将符合 UUID v4 约束的随机字节格式化为标准 UUID 字符串。
 *
 * @param bytes - 已设置版本位与变体位的 16 字节数组。
 * @returns 标准 UUID v4 字符串。
 */
function formatUuidV4(bytes: Uint8Array): string {
  const segments = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));

  return [
    segments.slice(0, 4).join(''),
    segments.slice(4, 6).join(''),
    segments.slice(6, 8).join(''),
    segments.slice(8, 10).join(''),
    segments.slice(10, 16).join(''),
  ].join('-');
}

/**
 * 使用 `crypto.getRandomValues()` 构造 UUID v4。
 *
 * 当运行环境缺失 `crypto.randomUUID()` 但仍提供安全随机源时，
 * 通过手动设置版本位与变体位保持 UUID 语义一致。
 *
 * @param cryptoApi - 当前环境可用的 Web Crypto 接口。
 * @returns UUID v4 字符串。
 */
function createUuidV4FromRandomValues(cryptoApi: Crypto): string {
  const bytes = new Uint8Array(UUID_V4_BYTE_LENGTH);
  cryptoApi.getRandomValues(bytes);

  // 按 RFC 4122 设置版本位与变体位，确保输出仍是合法 UUID v4。
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  return formatUuidV4(bytes);
}

/**
 * 在完全缺失 Web Crypto 时使用非加密随机数兜底生成 UUID v4。
 *
 * 该分支仅用于极端兼容场景，目标是保证 API 可用性，
 * 而不是替代加密级随机源。
 *
 * @returns UUID v4 字符串。
 */
function createUuidV4FromMathRandom(): string {
  const bytes = new Uint8Array(UUID_V4_BYTE_LENGTH);

  for (let index = 0; index < UUID_V4_BYTE_LENGTH; index += 1) {
    bytes[index] = Math.floor(Math.random() * 256);
  }

  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  return formatUuidV4(bytes);
}

/**
 * 生成 UUID v4 字符串。
 *
 * 优先使用运行环境原生 `crypto.randomUUID()`；
 * 若不可用，则退化到 `crypto.getRandomValues()`；
 * 最后再使用 `Math.random()` 保证接口在极端环境下仍可工作。
 *
 * @returns UUID v4 字符串。
 */
function createUuidV4(): string {
  const cryptoApi = getCryptoApi();

  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }

  if (cryptoApi?.getRandomValues) {
    return createUuidV4FromRandomValues(cryptoApi);
  }

  return createUuidV4FromMathRandom();
}

/**
 * 生成唯一标识。
 *
 * @returns UUID v4 字符串。
 */
export function generateId(): string {
  return createUuidV4();
}

/**
 * 生成客户端标识。
 *
 * @returns `client_{timestamp}_{random}` 格式的客户端 ID。
 */
export function generateClientId(): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).slice(2, 11);
  return `client_${timestamp}_${random}`;
}

/**
 * 判断 ID 是否为有效 UUID v4。
 *
 * @param id - 待校验的 ID。
 * @returns 是否为有效 UUID v4。
 */
export function isValidId(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);
}

/**
 * 生成短 ID。
 *
 * @returns 8 位短 ID。
 */
export function generateShortId(): string {
  return createUuidV4().slice(0, 8);
}
